import React from 'react';
import { X, Download } from 'lucide-react';
import { triggerPdfDownload } from '../utils/pdfGenerator';

const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth < 768;

/**
 * Preview PDF in a modal; on mobile, "Save / Download" uses Web Share API so users can Save to Files.
 */
export default function PdfPreviewModal({ open, pdfUrl, title, downloadFilename, onClose }) {
  if (!open || !pdfUrl) return null;

  const filename = downloadFilename || 'document.pdf';
  const heading = title || 'Document preview';

  const handleSaveDownload = async () => {
    const urlToRevoke = pdfUrl;
    try {
      if (isMobileViewport() && typeof navigator.share === 'function') {
        const blob = await fetch(pdfUrl).then((r) => r.blob());
        const file = new File([blob], filename, { type: 'application/pdf' });
        const sharePayload = { files: [file], title: heading };
        if (!navigator.canShare || navigator.canShare(sharePayload)) {
          try {
            await navigator.share(sharePayload);
          } catch (shareErr) {
            if (shareErr?.name !== 'AbortError') triggerPdfDownload(pdfUrl, filename);
          }
        } else {
          triggerPdfDownload(pdfUrl, filename);
        }
      } else {
        triggerPdfDownload(pdfUrl, filename);
      }
    } catch (e) {
      triggerPdfDownload(pdfUrl, filename);
    }
    setTimeout(() => URL.revokeObjectURL(urlToRevoke), 300);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 dark:bg-black/70"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-dark-card rounded-xl shadow-xl border border-gray-200 dark:border-dark-border flex flex-col w-full max-w-4xl h-[95dvh] sm:h-[90vh] sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 p-3 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-dark-text">{heading}</h3>
          <button
            type="button"
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-dark-muted dark:hover:text-dark-text rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 p-2 sm:p-3 overflow-hidden flex flex-col">
          <iframe
            src={`${pdfUrl}#view=Fit`}
            title={heading}
            className="w-full h-full min-h-0 rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg"
          />
        </div>
        <div className="flex-shrink-0 p-3 border-t border-gray-200 dark:border-dark-border flex gap-2 justify-end">
          <button
            type="button"
            className="px-4 py-2 text-gray-700 dark:text-dark-text bg-gray-100 dark:bg-dark-bg rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 font-medium"
            onClick={onClose}
          >
            Close
          </button>
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            onClick={handleSaveDownload}
          >
            <Download size={18} />
            Save / Download
          </button>
        </div>
      </div>
    </div>
  );
}
